import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faShareAlt, faCog, faPlusCircle, faArchive, faEnvelopeOpenText, faCertificate, faUnlock, faMoon, faSun, faBars, faTimes } from '@fortawesome/free-solid-svg-icons'
import { Scrollbars } from 'react-custom-scrollbars-4';

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableItemProps {
  note: NoteInfo;
  isDisabled: boolean;
  onMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
  matches: NoteMatch[] | null;
}

const SortableItem: React.FC<SortableItemProps> = ({ note, isDisabled, onMouseDown, matches }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: note.id, disabled: isDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...getNoteStyle(note)
  };

  let itemClass = 'note-tab note-tab-general';
  if (note.id === (window as any)._activeNoteId) {
    itemClass += ' current';
  }

  let iconClass = "share-status";
  if (note.shareAsReadOnly) {
    iconClass += ' share-readonly';
  }

  const match = matches?.find(x => x.noteId === note.id);

  return (
    <li
      ref={setNodeRef}
      data-id={note.id}
      className={itemClass}
      style={style}
      onMouseDown={(e) => onMouseDown(e, note)}
      {...attributes}
      {...listeners}
    >
      <div>
        <strong className="icon"><FontAwesomeIcon className={iconClass} icon={note.isPrivate ? faLock : faShareAlt}/></strong> 
        <span className="note-title">{note.name || untitledNoteTitle}</span>
        {note.isProtected && <FontAwesomeIcon className="icon-protected" icon={faCertificate}/>}
        <ImgGear className="options-note context-menu-target" size={16}/>
        {match && match.parts.map((m, index) => <span key={index} className="search-match" dangerouslySetInnerHTML={{__html: m}}></span>)}
      </div>
    </li>
  );
};

const getNoteStyle = (note: NoteInfo): React.CSSProperties => {
  const color = note.color;
  if (!color || !color.match(/#[0-9A-Fa-f]{6}/g)){
    return {};
  }
  
  const r = parseInt(color.substring(1, 2), 16);
  const g = parseInt(color.substring(3, 2), 16);
  const b = parseInt(color.substring(5, 2), 16);

  return {
    borderLeftColor: color,
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.1)`
  };
};

interface SortableNotesListProps {
  notes: NoteInfo[];
  filterText: string;
  matches: NoteMatch[] | null;
  isTouchDevice: boolean;
  onMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

const SortableNotesList: React.FC<SortableNotesListProps> = ({ notes, filterText, matches, isTouchDevice, onMouseDown, onDragEnd }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
        delay: isTouchDevice ? 200 : 100,
        tolerance: 10
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={notes.map(n => n.id)}
        strategy={verticalListSortingStrategy}
      >
        <Scrollbars hideTracksWhenNotNeeded autoHide className="scrollbars-wrapper">
          <ul className="notes-list">
            {notes.map((note) => (
              <SortableItem
                key={note.id}
                note={note}
                isDisabled={!!filterText}
                onMouseDown={onMouseDown}
                matches={matches}
              />
            ))}
          </ul>
        </Scrollbars>
      </SortableContext>
    </DndContext>
  );
};

const untitledNoteTitle = 'Untitled note';

import { type CategoryInfo } from '../models/CategoryInfo';
import { type NotesResponse } from '../models/NotesResponse';
import { type NoteInfo } from '../models/NoteInfo';
import { PromiseCompletion } from '../classes/PromiseCompletion';

import { themeService } from '../services/ThemeService';
import { apiService } from '../services/ApiService';
import { notificationService } from '../services/NotificationService';
import { ArrayService } from '../services/ArrayService';

const RenameNoteDialog = React.lazy(() => import('./dialogs/RenameNoteDialog'));
import { type RenameNoteDialogProps } from './dialogs/RenameNoteDialog';

const ColorNoteDialog = React.lazy(() => import('./dialogs/ColorNoteDialog'));
import { type ColorNoteDialogProps } from './dialogs/ColorNoteDialog';

const ShareNoteDialog = React.lazy(() => import('./dialogs/ShareNoteDialog'));
import { type ShareNoteDialogProps } from './dialogs/ShareNoteDialog';

const ChangeCategoryDialog = React.lazy(() => import('./dialogs/ChangeCategoryDialog'));
import { type ChangeCategoryDialogProps, type ChangeCategoryNoteParameters } from './dialogs/ChangeCategoryDialog';

const CreateNoteDialog = React.lazy(() => import('./dialogs/CreateNoteDialog'));
import { type CreateNoteDialogProps, type CreateNoteParameters } from './dialogs/CreateNoteDialog';
import Page404 from './Page404';

const ManageCategoriesDialog = React.lazy(() => import('./dialogs/ManageCategoriesDialog'));
import { type ManageCategoriesDialogProps } from './dialogs/ManageCategoriesDialog';
import CategoryPicker, { type CategoryContext } from '../components/CategoryPicker';
import ContextMenu, { ContextMenuItem, ContextMenuSeparator } from '../components/ContextMenu';
import { ModalButtonType, modalService } from '../components/Modal';
import Editor from '../components/Editor';
import { extensions } from '@services/Extensions';
import { ImgGear } from '@components/Images/ImgGear';

type NoteMatch = {
  noteId: string;
  parts: string[];
};

@observer
export default class Notes extends React.Component<unknown> {
  @observable private _notes: NoteInfo[] = [];
  @observable private _categories: CategoryInfo[] = [];
  @observable private _activeCategoryId = '';
  @observable private _activeNoteId = '';
  @observable private _filterText = '';
  @observable.ref private _matches: NoteMatch[] | null = null;
  @observable private _isArchive = false;
  @observable private _searchContent = false;
  @observable private _isUnlocked = false;
  @observable private _editorHeaderHeight = 41;
  @observable private _isMobileSidebarVisible = false;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _notesListRef: React.RefObject<HTMLDivElement> = React.createRef();
  private _searchCancellationSource: AbortController;

  constructor(props: unknown){
    super(props);
    makeObservable(this);
    this._searchCancellationSource = new AbortController();
  }

  async componentDidMount(): Promise<void>{
    await this._loadData(true);
    setTimeout(this._recalculateScrollSize, 100);
    window.addEventListener('resize', this._onWindowResize);
    window.addEventListener('blur', this._onBlur);
  }

  componentWillUnmount(){
    window.removeEventListener('resize', this._onWindowResize);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('blur', this._onBlur);
    document.body.classList.remove('mobile-sidebar-open');
  }

  render(): JSX.Element{
    const notes = this.Notes.filter(x => (x.name ?? untitledNoteTitle)?.toLowerCase()?.includes(this._filterText.toLowerCase()) !== false || this._matches?.find(m => m.noteId === x.id));
    const sortedNotes = notes.sort((n1, n2) => n1.sortOrder - n2.sortOrder);
    const isTouchDevice = this._isTouchDevice();

    const activeNote = this._notes.find(x => x.id === this._activeNoteId);

    // Store activeNoteId in window for SortableItem access
    (window as any)._activeNoteId = this._activeNoteId;

    return (
      <div className="notes-container">
        {/* Mobile overlay */}
        <div 
          className={`mobile-sidebar-overlay${this._isMobileSidebarVisible ? ' visible' : ''}`}
          onClick={this._onToggleMobileSidebar}
        ></div>
        
        {/* Scrollbars: https://codesandbox.io/s/orLx4ZlL?file=/index.js:91-114 */}
        <div className={`note-list-container${this._isMobileSidebarVisible ? ' mobile-visible' : ''}`} ref={this._notesListRef}>
          <div>
            <div className="notes-filter">
              <input className="form-control" placeholder="🔎︎ Search" value={this._filterText} onChange={e => {
                this._filterText = e.target.value;
                if (this._filterText){
                  this._startSearch();
                } else {
                  this._matches = null;
                }
              }}/>
              {!!this._filterText && <span title="Search inside content of notes" className={'archive-search-content' + (this._searchContent ? ' active' : '')} onClick={this._onSearchContentToggle}>
                <FontAwesomeIcon icon={faEnvelopeOpenText} className='fa-icon'/>
              </span>}
              <span title="Show archived notes" className={'archive-toggle' + (this._isArchive ? ' active' : '')} onClick={this._onArchiveToggle}>
                <FontAwesomeIcon icon={faArchive} className='fa-icon'/>
              </span>
            </div>
          </div>
          {this._categories.length > 1 && !this._filterText.startsWith('@') && <CategoryPicker 
            categories={this._categories} 
            activeCategoryId={this._activeCategoryId}
            onRenderItems={(c) => <div className="item item-settings" onClick={() => this._onCategorySettingsClick(c)}>Settings <FontAwesomeIcon icon={faCog} className='fa-icon'/></div>}
            onCategoryChanged={this._onCategoryChanged}
          />}
          <ContextMenu 
            container={this._notesListRef}
            onAppear={e => !e.classList.contains('form-control')}
          >
            {(element) => this._renderMenu(element)}
          </ContextMenu>
          <SortableNotesList
            notes={sortedNotes}
            filterText={this._filterText}
            matches={this._matches}
            isTouchDevice={isTouchDevice}
            onMouseDown={this._onNoteMouseDown}
            onDragEnd={this._handleDragEnd}
          />
          <div className="note-tab note-tab-new" onClick={this._addNewNote}>
            <strong><FontAwesomeIcon icon={faPlusCircle} className='fa-icon' /> Add new</strong>
          </div>
          <div className="nav-footer">
            <span className="nav-footer-user" title={this._userName}>{this._userName}</span>
            <span className="nav-footer-theme" onClick={themeService.toggle} title={themeService.isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
              <FontAwesomeIcon icon={themeService.isDark ? faSun : faMoon} className='fa-icon'/>
            </span>
          </div>
        </div>
        
        {/* Mobile sidebar toggle button */}
        <button 
          className="mobile-sidebar-toggle" 
          onClick={this._onToggleMobileSidebar}
          title={this._isMobileSidebarVisible ? 'Close menu' : 'Open menu'}
        >
          <FontAwesomeIcon icon={this._isMobileSidebarVisible ? faTimes : faBars} />
        </button>
        
        <style>
          {`#snow-container .ql-container {  height: calc(100vh - ${this._editorHeaderHeight}px) !important; height: calc(var(--vh, 1vh) * 100 - ${this._editorHeaderHeight}px)!important; }`}
        </style>
        {!!this._activeNoteId && <Editor 
          key={this._activeNoteId} 
          noteId={this._activeNoteId} 
          notFoundView={Page404} 
          sharedView={false}
          onDataLoaded={() => setTimeout(this._onWindowResize, 0)}
        />}
        {activeNote?.isProtected && !this._isUnlocked && <div className="protected-screen">
          <button className="btn btn-success" onClick={this._onUnlockClick}>
            Unlock <FontAwesomeIcon icon={faUnlock} className='fa-icon'/>
          </button>
        </div>}
      </div>
    );
  }
  
  private _renderMenu(element: HTMLElement): JSX.Element | null{
    const activityCodeTarget = element.closest('.note-tab') as HTMLDivElement;
    if (activityCodeTarget){
      const id = activityCodeTarget.dataset.id as string;
      const note = this._notes.find(x => x.id === id);

      return (
        <>
          <ContextMenuItem title="Rename note" onClick={() => this._renameNote(id)} />
          <ContextMenuItem title="Share" onClick={() => this._shareNote(id)} />
          <ContextMenuItem title="Color" onClick={() => this._colorNote(id)} />
          <ContextMenuItem title={note?.isProtected ? "Unprotect" : "Protect"} onClick={() => this._toggleProtected(id, !!note?.isProtected)} />
          <ContextMenuSeparator/>
          <ContextMenuItem title="Category" onClick={() => this._moveToCategory(id)} />
          {!note?.isArchived && <ContextMenuItem title="Archive" onClick={() => this._archiveNote(id)} />}
          {note?.isArchived && <ContextMenuItem title="Undo Archive" onClick={() => this._undoArchiveNote(id)} />}
          <ContextMenuSeparator/>
          <ContextMenuItem title="Delete note" onClick={() => this._deleteNote(id)} />
        </>
      );
    }

    return null;
  }

  private _isTouchDevice() {
    return (('ontouchstart' in window) ||  (navigator.maxTouchPoints > 0));
  }

  @computed
  private get Notes() {
    const isFullFilter = this._filterText && this._filterText.startsWith('@');
    if (isFullFilter){
      return this._notes;
    }
    return this._notes.filter(x => (x.categoryId ?? '') === (this._activeCategoryId ?? ''));
  }

  @computed
  private get _userName(): string {
    return window.user?.name ?? window.user?.email ?? 'Guest';
  }

  private _onWindowResize = () => {
    this._recalculateScrollSize();
  }

  private _recalculateScrollSize = () => {
    const header = document.getElementsByClassName('ql-toolbar')[0];
    this._editorHeaderHeight = header?.getBoundingClientRect()?.height || 41;
  }

  @action.bound
  private async _onCategorySettingsClick(context: CategoryContext){
    context.close();

    await modalService.show<ManageCategoriesDialogProps, string>(ManageCategoriesDialog, {
      onRefresh: () => this._refreshCategories()
    });
  }
  
  @action.bound
  private _onNoteMouseDown(e: React.MouseEvent, note: NoteInfo){
    if (e.button === 1 /* Middle */) {
      e.preventDefault();
      e.stopPropagation();
      window.open('/?noteId=' + note.id , '_blank')?.focus();
      return;
    }

    const isFilterActive = !!this._filterText;
    // if filter active - select also category of the note
    if (isFilterActive && note.categoryId !== this._activeCategoryId){
      this._activeCategoryId = note.categoryId ?? '';
    }
    
    this._activeNoteId = note.id;
    this._isUnlocked = false;
    this._setTitle(note.name);
    
    const isRightClick = e.button === 2;

    // Close mobile sidebar when selecting a note
    // check for .context-menu-target as target and ignore if click is on it, to allow opening context menu without closing sidebar
    if (!isRightClick && window.innerWidth <= 768 && !((e.target as HTMLElement).closest('.context-menu-target'))) {
      this._isMobileSidebarVisible = false;
    }
  }

  @action.bound
  private _onToggleMobileSidebar(){
    this._isMobileSidebarVisible = !this._isMobileSidebarVisible;
    
    // Prevent body scrolling when sidebar is open on mobile
    if (window.innerWidth <= 768) {
      if (this._isMobileSidebarVisible) {
        document.body.classList.add('mobile-sidebar-open');
      } else {
        document.body.classList.remove('mobile-sidebar-open');
      }
    }
  }

  @action.bound
  private _onUnlockClick(){
    this._isUnlocked = true;
  }

  @action.bound
  private _onVisibilityChange(){
    if (document.visibilityState == 'hidden') {
      this._isUnlocked = false;
    }
  }

  @action.bound
  private _onBlur(){
    this._isUnlocked = false;
  }

  @action.bound
  private async _onArchiveToggle(){
    if (this._loader.isPending) return;

    this._isArchive = !this._isArchive;
    await this._loadData(false);
    if (this._filterText && this._searchContent){
      this._performSearch();
    }
  }

  @action.bound
  private async _onCategoryChanged(e: CategoryInfo){
    this._activeCategoryId = e.id;
  }

  @action.bound
  private async _onSearchContentToggle(){
    this._searchContent = !this._searchContent;
    if (this._searchContent){
      this._performSearch();
    } else {
      this._matches = null;
    }
  }
  
  private async _loadData(init: boolean): Promise<void>{
    const parameters = { 
      isArchive: init ? null : (this._isArchive ? 'true' : 'false') 
    };

    const notesResponse = await apiService.get<NotesResponse>('/api/note', parameters, {
      loader: this._loader
    });

    runInAction(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const openNoteId = urlParams.get('noteId');
      const qsNoteId = !!notesResponse.notes.find(x => x.id === openNoteId) && openNoteId ? openNoteId : null;
      if (qsNoteId){
        urlParams.delete('noteId');
        window.history.replaceState({}, document.title, window.location.pathname + urlParams.toString());      
      }

      this._activeCategoryId = notesResponse.activeCategoryId ?? '';

      const categories = notesResponse.categories;
      if (notesResponse.notes.find(x => !x.categoryId)){
        categories.push({
          id: '',
          name: 'Default',
          sortOrder: 0
        });
      }
      this._categories = categories;
      this._notes = notesResponse.notes;
      this._activeNoteId = qsNoteId ?? notesResponse.activeNoteId ?? '';
      
      if (init){
        this._isArchive = !!notesResponse.activeNoteArchived;
        const activeNote = notesResponse.notes.find(x => x.id === notesResponse.activeNoteId);
        if (activeNote){
          this._setTitle(activeNote.name);
        }
      }
    });
  }

  private async _refreshCategories(){
    const categories = await apiService.get<CategoryInfo[]>('/api/note/category', null, {
      loader: this._loader
    });

    runInAction(() => {
      this._categories = categories;
    });
  }

  private _setTitle(noteName: string){
    document.title = 'Notes - ' + noteName;
  }

  @action.bound
  private async _addNewNote(): Promise<void>{

    const { button, result } = await modalService.show<CreateNoteDialogProps, CreateNoteParameters>(CreateNoteDialog as unknown as React.FunctionComponent<CreateNoteDialogProps>, {});
    if (!result || button !== ModalButtonType.Create){
      return;
    }

    const newNote = await apiService.post<NoteInfo>('/api/note', { ...result, categoryId: this._activeCategoryId }, {
      loader: this._loader
    });

    this._notes.unshift(newNote);
    this._activeNoteId = newNote.id;
    this._setTitle(newNote.name);
  }

  @action.bound
  private async _deleteNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation("Confirmation", `Do you really want to delete note (${note.name ?? untitledNoteTitle})`, { color: 'danger', buttonTitle: 'Remove' })){
      return;
    }

    await apiService.delete('/api/note/' + id, {
      loader: this._loader
    });

    const noteIndex = this._notes.findIndex(n => n.id === id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
    }
    const activeNote = this._notes[0];
    if (activeNote){
      this._setTitle(activeNote.name);
    }
    this._activeNoteId = activeNote?.id;
  }

  @action.bound
  private async _renameNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }
    
    const { button, result } = await modalService.show<RenameNoteDialogProps, string>(RenameNoteDialog, {
      note: note
    });

    if (button !== ModalButtonType.Ok || !result){
      return;
    }
    
    await apiService.put('/api/note/' + id + '/name', { name: result }, {
      loader: this._loader
    });

    note.name = result;
  }
  
  @action.bound
  private async _colorNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }
    
    const { button, result } = await modalService.show<ColorNoteDialogProps, string>(ColorNoteDialog, {
      note: note
    });

    if (button !== ModalButtonType.Ok || !result){
      return;
    }
    
    await apiService.put<NoteInfo>('/api/note/' + id + '/color', { color: result }, {
      loader: this._loader
    });

    note.color = result;
  }

  @action.bound
  private async _shareNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    await modalService.show<ShareNoteDialogProps, string>(ShareNoteDialog, {
      note: note,
      onUpdate: (e) => {
        note.isPrivate = !e.shareId;
        note.shareId = e.shareId;
        note.shareAsReadOnly = e.isReadOnly;
      }
    });

  }

  @action.bound
  private async _archiveNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to archive note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/archive', null, {
      loader: this._loader
    });

    const noteIndex = this._notes.findIndex(x => x.id === note.id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
      const activeNote = this._notes[noteIndex];
      this._activeNoteId = activeNote?.id;
      activeNote && this._setTitle(activeNote.name);
    }
  }

  @action.bound
  private async _moveToCategory(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    const { button, result } = await modalService.show<ChangeCategoryDialogProps, ChangeCategoryNoteParameters>(ChangeCategoryDialog, {
      note: note
    });
    
    if (button === ModalButtonType.Create && result){
      note.categoryId = result.id;
      this._activeCategoryId = result.id;
    }
  }

  @action.bound
  private async _toggleProtected(id: string, isProtected: boolean): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to ${isProtected ? 'unprotect' : 'protect'} note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/toggle-protect', null, {
      loader: this._loader
    });

    note.isProtected = !note.isProtected;
  }

  @action.bound
  private async _undoArchiveNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to activate note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/undo-archive', null, {
      loader: this._loader
    });

    const noteIndex = this._notes.findIndex(x => x.id === note.id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
      const activeNote = this._notes[noteIndex];
      this._activeNoteId = activeNote?.id;
      activeNote && this._setTitle(activeNote.name);
    }
  }

  @action.bound
  private async _handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const notes = this.Notes.filter(x => (x.name ?? untitledNoteTitle)?.toLowerCase()?.includes(this._filterText.toLowerCase()) !== false || this._matches?.find(m => m.noteId === x.id));
    const sortedNotes = notes.sort((n1, n2) => n1.sortOrder - n2.sortOrder);

    const oldIndex = sortedNotes.findIndex(n => n.id === active.id);
    const newIndex = sortedNotes.findIndex(n => n.id === over.id);

    ArrayService.arrayMoveMutable(sortedNotes, oldIndex, newIndex);
    sortedNotes.forEach((w, index) => w.sortOrder = index);

    const allSortedNotes = this._notes.sort((n1, n2) => n1.sortOrder - n2.sortOrder);
    const orderModel = {
      ids: allSortedNotes.map(w => w.id)
    };
    await apiService.put('/api/note/reorder', orderModel);
    notificationService.success('Order was saved!');
  }

  private _startSearch(){
    if (!this._filterText){
      this._matches = null;
      return;
    }
    extensions.executeTimeout(this._performSearch, 300);
  }

  private _cancelSearch(): void{
    this._searchCancellationSource.abort();
    this._searchCancellationSource = new AbortController();
  }

  private _performSearch = async () => {
    this._cancelSearch();

    if (!this._filterText){
      return;
    }

    const searchText = this._filterText.startsWith('@') ? this._filterText.substring(1) : this._filterText;
    const { matches } = await apiService.post<{text: string, matches: NoteMatch[]}>('/api/note/search', { text: searchText, isArchive: this._isArchive ? 'true' : '' }, {
      cancellationToken: this._searchCancellationSource.signal,
      loader: this._loader
    });

    this._matches = matches;
  }
}